// The structured admission assessment.
//
// Logical sections that expand, not one long form — the brief's rule, and the
// practical one: an admission workup is filled in over hours by more than one
// person, so it has to be resumable rather than a single sitting.
//
// The allergy and medication blocks do NOT capture lists. They record that a
// REVIEW happened, and link to the real allergies/medications record. Copying
// those lists onto the admission would create a snapshot that goes stale while
// looking authoritative.
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input, NativeSelect, Textarea } from '@/components/ui/Input';
import { LoadingState } from '@/components/ui/States';
import { TextPromptDialog } from '@/components/ui/TextPromptDialog';
import VoiceFormFiller, { type VoiceField } from '@/components/VoiceFormFiller';
import { api, ApiError } from '@/lib/api/client';
import {
  appendText, mergeTextFields, numField, selectField, textField,
  vBool, vMatrix, vNum, vOneOf, vText, yesNoField, type Extracted,
} from '@/lib/voiceFill';
import { parseComplaints, DURATION_UNITS } from '../voice';
import { useAssessment, useSaveAssessment, useSignAssessment, useRecordReview, useDiagnoses, useCreateDiagnosis, useSupersedeDiagnosis } from '../hooks';
import type {
  AssessmentInput, CaseSheet, PastHistoryFlags, GeneralSigns, GeneralSignStatus, TriFlag,
  ChiefComplaint, Addiction, DiagnosisRole,
} from '../types';
import { Section, Empty, fmtDateTime, useCan, BackdateField } from '../ui';

const NARRATIVE_FIELDS: { key: keyof AssessmentInput; label: string; rows?: number; hint?: string }[] = [
  { key: 'admission_reason', label: 'Reason for admission', rows: 2 },
  { key: 'presenting_complaint', label: 'Presenting complaint', rows: 2 },
  { key: 'history_present_illness', label: 'History of present illness', rows: 3 },
  { key: 'past_medical_history', label: 'Past medical history', rows: 2 },
  { key: 'surgical_history', label: 'Surgical history', rows: 2 },
  { key: 'family_history', label: 'Family history', rows: 2 },
  { key: 'social_history', label: 'Social history', rows: 2 },
  { key: 'general_examination', label: 'General examination', rows: 2 },
  { key: 'provisional_diagnosis', label: 'Provisional diagnosis', rows: 2 },
  { key: 'clinical_impression', label: 'Clinical impression', rows: 2 },
  { key: 'initial_plan', label: 'Initial treatment plan', rows: 3 },
];

// One dictation covering the whole assessment ("reason for admission is...,
// history of present illness is...") rather than a mic per field.
const NARRATIVE_VOICE_FIELDS: VoiceField[] = NARRATIVE_FIELDS.map(f => ({
  key: String(f.key), label: f.label, type: 'text',
}));

const SYSTEMS = [
  { key: 'cvs', label: 'Cardiovascular' },
  { key: 'rs', label: 'Respiratory' },
  { key: 'cns', label: 'Central nervous' },
  { key: 'pa', label: 'Abdomen' },
  { key: 'other', label: 'Other' },
];

const RISKS = [
  { key: 'fall_risk', label: 'Fall risk' },
  { key: 'pressure_ulcer_risk', label: 'Pressure ulcer risk' },
  { key: 'vte_risk', label: 'VTE risk' },
];

// Phase 3 of docs/HMS_V4_IP_CASE_SHEET_INDIA_DESIGN.md (§6.6).
const PAST_HISTORY_ITEMS: { key: keyof PastHistoryFlags; label: string }[] = [
  { key: 'dm', label: 'DM' }, { key: 'htn', label: 'HTN' }, { key: 'cad', label: 'CAD' },
  { key: 'tb', label: 'TB' }, { key: 'asthma_copd', label: 'Asthma/COPD' }, { key: 'epilepsy', label: 'Epilepsy' },
  { key: 'thyroid', label: 'Thyroid' }, { key: 'ckd', label: 'CKD' }, { key: 'cancer', label: 'Cancer' },
];
const GENERAL_SIGN_ITEMS: { key: keyof GeneralSigns; label: string }[] = [
  { key: 'pallor', label: 'Pallor' }, { key: 'icterus', label: 'Icterus' }, { key: 'cyanosis', label: 'Cyanosis' },
  { key: 'clubbing', label: 'Clubbing' }, { key: 'lymphadenopathy', label: 'LN' },
  { key: 'oedema', label: 'Oedema' }, { key: 'dehydration', label: 'Dehydration' },
];

const PERSONAL_KEYS = ['diet', 'appetite', 'sleep', 'bowel', 'bladder'] as const;
const RISK_LEVELS = ['low', 'moderate', 'high'] as const;
const PAST_HISTORY_SPOKEN: Partial<Record<keyof PastHistoryFlags, string>> = {
  dm: 'Diabetes (DM)', htn: 'Hypertension (HTN)', cad: 'Coronary artery disease (CAD)',
  tb: 'Tuberculosis (TB)', ckd: 'Chronic kidney disease (CKD)',
};

// The same single dictation also reaches the structured half of the workup —
// a doctor naturally says "known diabetic and hypertensive, no TB; pallor
// present, no icterus; CVS S1 S2 heard; GCS 15" in the same breath as the
// history, and those used to be dropped because only the narrative boxes
// were offered to the extractor. Everything lands in the form for review.
const ASSESSMENT_VOICE_FIELDS: VoiceField[] = [
  ...NARRATIVE_VOICE_FIELDS,
  textField('chief_complaints', 'Chief complaints, each as "complaint - N days/weeks/months", separated by semicolons'),
  {
    key: 'past_history_flags', label: 'Past history — known conditions', type: 'choice-matrix',
    rows: PAST_HISTORY_ITEMS.map(i => ({ key: i.key, label: PAST_HISTORY_SPOKEN[i.key] ?? i.label, options: ['yes', 'no', 'unknown'] })),
  },
  {
    key: 'general_signs', label: 'General signs on examination', type: 'choice-matrix',
    rows: GENERAL_SIGN_ITEMS.map(i => ({ key: i.key, label: i.key === 'lymphadenopathy' ? 'Lymphadenopathy' : i.label, options: ['present', 'absent'] })),
  },
  textField('sys_cvs', 'Systemic examination — cardiovascular (CVS)'),
  textField('sys_rs', 'Systemic examination — respiratory (RS / chest)'),
  textField('sys_cns', 'Systemic examination — central nervous system (CNS)'),
  textField('sys_pa', 'Systemic examination — abdomen (P/A)'),
  textField('sys_other', 'Systemic examination — other systems'),
  ...PERSONAL_KEYS.map(k => textField(`ph_${k}`, `Personal history — ${k}`)),
  numField('gcs_e', 'GCS eye opening score 1-4'),
  numField('gcs_m', 'GCS motor score 1-6'),
  numField('gcs_v', 'GCS verbal score 1-5'),
  numField('gcs_total', 'GCS total out of 15, only if components were not given'),
  textField('differential_diagnosis', 'Differential diagnosis'),
  numField('expected_los_days', 'Expected length of stay in days'),
  yesNoField('counselling_done', 'Patient / attender counselled'),
  selectField('risk_fall_risk', 'Fall risk level', RISK_LEVELS),
  selectField('risk_pressure_ulcer_risk', 'Pressure ulcer risk level', RISK_LEVELS),
  selectField('risk_vte_risk', 'VTE risk level', RISK_LEVELS),
];

/** Score in range, else undefined — a misheard "GCS motor 16" must not land. */
const inRange = (n: number | undefined, min: number, max: number) =>
  n !== undefined && Number.isInteger(n) && n >= min && n <= max ? n : undefined;

/** Apply one assessment dictation onto the draft. Free text is appended;
 * structured answers only set what was actually said. */
function applyAssessmentVoice(p: AssessmentInput, x: Extracted): AssessmentInput {
  const next: AssessmentInput = {
    ...p,
    ...mergeTextFields(p, x, [...NARRATIVE_FIELDS.map(f => f.key as keyof AssessmentInput & string), 'differential_diagnosis']),
  };

  const complaints = vText(x, 'chief_complaints');
  if (complaints) {
    const existing = p.chief_complaints ?? [];
    const seen = new Set(existing.map(c => c.text.toLowerCase()));
    next.chief_complaints = [...existing, ...parseComplaints(complaints).filter(c => !seen.has(c.text.toLowerCase()))];
  }

  const flags = vMatrix(x, 'past_history_flags');
  if (Object.keys(flags).length) {
    const merged: PastHistoryFlags = { ...(p.past_history_flags ?? {}) };
    for (const [k, v] of Object.entries(flags)) {
      const key = k as keyof PastHistoryFlags;
      if (v === 'yes' || v === 'no' || v === 'unknown') merged[key] = { ...merged[key], v };
    }
    next.past_history_flags = merged;
  }

  const signs = vMatrix(x, 'general_signs');
  if (Object.keys(signs).length) {
    const merged: GeneralSigns = { ...(p.general_signs ?? {}) };
    for (const [k, v] of Object.entries(signs)) {
      const key = k as keyof GeneralSigns;
      if (v === 'present' || v === 'absent') merged[key] = { ...merged[key], status: v };
    }
    next.general_signs = merged;
  }

  const sys: Record<string, string> = { ...((p.systemic_examination as Record<string, string> | null | undefined) ?? {}) };
  let sysChanged = false;
  for (const s of SYSTEMS) {
    const t = vText(x, `sys_${s.key}`);
    if (t) { sys[s.key] = appendText(sys[s.key], t, '; '); sysChanged = true; }
  }
  if (sysChanged) next.systemic_examination = sys as AssessmentInput['systemic_examination'];

  const ph = { ...(p.personal_history ?? {}) };
  let phChanged = false;
  for (const k of PERSONAL_KEYS) {
    const t = vText(x, `ph_${k}`);
    if (t) { ph[k] = appendText(ph[k], t, '; '); phChanged = true; }
  }
  if (phChanged) next.personal_history = ph;

  const e = inRange(vNum(x, 'gcs_e'), 1, 4);
  const m = inRange(vNum(x, 'gcs_m'), 1, 6);
  const v = inRange(vNum(x, 'gcs_v'), 1, 5);
  if (e) next.gcs_e = e;
  if (m) next.gcs_m = m;
  if (v) next.gcs_v = v;
  // "GCS 15" said without components can only mean 4/6/5 — any other total
  // is ambiguous, so it is left for the doctor to split.
  if (!e && !m && !v && vNum(x, 'gcs_total') === 15) { next.gcs_e = 4; next.gcs_m = 6; next.gcs_v = 5; }

  const los = vNum(x, 'expected_los_days');
  if (los !== undefined && los >= 0 && los < 366) next.expected_los_days = Math.round(los);
  const counselled = vBool(x, 'counselling_done');
  if (counselled !== undefined) next.counselling_done = counselled;

  const risk: Record<string, string> = { ...((p.risk_assessment as Record<string, string> | null | undefined) ?? {}) };
  let riskChanged = false;
  for (const r of RISKS) {
    const level = vOneOf(x, `risk_${r.key}`, RISK_LEVELS);
    if (level) { risk[r.key] = level; riskChanged = true; }
  }
  if (riskChanged) next.risk_assessment = risk as AssessmentInput['risk_assessment'];
  return next;
}

/** One tri-state chip row (Yes/No/Unknown or Present/Absent/Not assessed) —
 * the design's own convention: default is never "Absent"/"No", it is the
 * unanswered state, so a sign nobody looked for is never printed as a
 * negative finding (§6.6's reversal note). */
function TriChip({ value, options, onChange, disabled }: {
  value: string | undefined; options: { value: string; label: string; tone: 'y' | 'n' | 'u' }[];
  onChange: (v: string) => void; disabled?: boolean;
}) {
  return (
    <div className="flex gap-1">
      {options.map((o) => (
        <button key={o.value} type="button" disabled={disabled}
          onClick={() => onChange(o.value)}
          className={`rounded-full border px-2 py-0.5 text-[10.5px] ${
            value === o.value
              ? o.tone === 'y' ? 'border-danger bg-danger-soft text-danger-ink'
                : o.tone === 'n' ? 'border-success bg-success-soft text-success-ink'
                : 'border-warn bg-warn-soft text-warn-ink'
              : 'border-border text-ink-muted'
          }`}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

function ChiefComplaintsEditor({ value, onChange }: { value: ChiefComplaint[]; onChange: (v: ChiefComplaint[]) => void }) {
  const update = (i: number, patch: Partial<ChiefComplaint>) =>
    onChange(value.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));
  const remove = (i: number) => onChange(value.filter((_, idx) => idx !== i));

  return (
    <div>
      <div className="mb-1 text-xs font-semibold">Chief complaints</div>
      <div className="space-y-1.5">
        {value.map((c, i) => (
          <div key={i} className="flex items-center gap-1.5">
            <Input className="flex-1" value={c.text} placeholder="e.g. Fever"
              onChange={e => update(i, { text: e.target.value })} />
            <Input type="number" min={0} className="w-16" value={c.duration_value ?? ''}
              onChange={e => update(i, { duration_value: e.target.value ? Number(e.target.value) : undefined })} />
            <NativeSelect className="w-28" value={c.duration_unit ?? 'days'}
              onChange={e => update(i, { duration_unit: e.target.value as ChiefComplaint['duration_unit'] })}>
              {DURATION_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
            </NativeSelect>
            <Button variant="ghost" size="sm" onClick={() => remove(i)}>✕</Button>
          </div>
        ))}
        <Button variant="ghost" size="sm" onClick={() => onChange([...value, { text: '', duration_unit: 'days' }])}>+ Complaint</Button>
      </div>
    </div>
  );
}

const ADDICTION_STATUSES = ['current', 'ex', 'never'] as const;

function AddictionsEditor({ value, onChange }: { value: Addiction[]; onChange: (v: Addiction[]) => void }) {
  const update = (i: number, patch: Partial<Addiction>) =>
    onChange(value.map((a, idx) => (idx === i ? { ...a, ...patch } : a)));
  const remove = (i: number) => onChange(value.filter((_, idx) => idx !== i));

  return (
    <div className="mt-2">
      <div className="mb-1 text-[11px] text-ink-muted">Addictions</div>
      <div className="space-y-1.5">
        {value.map((a, i) => (
          <div key={i} className="flex items-center gap-1.5">
            <Input className="flex-1" placeholder="Substance (e.g. Smoking, Alcohol)" value={a.substance}
              onChange={e => update(i, { substance: e.target.value })} />
            <Input className="w-28" placeholder="Qty" value={a.quantity ?? ''} onChange={e => update(i, { quantity: e.target.value })} />
            <Input className="w-24" placeholder="Duration" value={a.duration ?? ''} onChange={e => update(i, { duration: e.target.value })} />
            <NativeSelect className="w-24" value={a.status} onChange={e => update(i, { status: e.target.value as Addiction['status'] })}>
              {ADDICTION_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </NativeSelect>
            <Button variant="ghost" size="sm" onClick={() => remove(i)}>✕</Button>
          </div>
        ))}
        <Button variant="ghost" size="sm" onClick={() => onChange([...value, { substance: '', status: 'never' }])}>+ Addiction</Button>
      </div>
    </div>
  );
}

export function AssessmentSection({ admissionId, readOnly, caseSheet }: {
  admissionId: number; readOnly: boolean; caseSheet: CaseSheet;
}) {
  const can = useCan();
  const { data, isLoading } = useAssessment(admissionId);
  const save = useSaveAssessment(admissionId);
  const sign = useSignAssessment(admissionId);
  const review = useRecordReview(admissionId);

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<AssessmentInput>({});
  const [err, setErr] = useState<string | null>(null);

  const mayWrite = can('ip:assessment') && !readOnly;
  const isSigned = data?.status === 'signed';
  const set = (k: keyof AssessmentInput, v: unknown) => setDraft(p => ({ ...p, [k]: v }));
  const setNested = (field: 'systemic_examination' | 'risk_assessment', k: string, v: string) =>
    setDraft(p => ({ ...p, [field]: { ...((p[field] as Record<string, string>) ?? {}), [k]: v } }));

  const startEdit = () => { setDraft(data ?? {}); setEditing(true); setErr(null); };
  const fail = (e: unknown) => setErr(e instanceof ApiError ? e.message : 'Something went wrong.');

  if (isLoading) return <LoadingState label="Loading assessment…" />;

  return (
    <div className="space-y-4">
      <Section
        focusId="assessment"
        title="Admission assessment"
        subtitle={data
          ? isSigned
            ? `Signed by ${data.signed_by} · ${fmtDateTime(data.signed_at)}`
            : 'Draft — not yet signed'
          : 'Not started'}
        action={
          <>
            {mayWrite && !editing && !isSigned && (
              <Button size="sm" data-focus="assessment:edit" onClick={startEdit}>{data ? 'Continue' : 'Start assessment'}</Button>
            )}
            {mayWrite && data && !isSigned && !editing && (
              <Button variant="success" size="sm" loading={sign.isPending} data-focus="assessment:sign"
                onClick={() => sign.mutate(undefined, { onError: fail })}>
                Sign
              </Button>
            )}
          </>
        }
      >
        {isSigned && (
          <div className="mb-3 rounded border border-success/40 bg-success-soft px-3 py-1.5 text-[11.5px] text-success-ink">
            Signed — this assessment can no longer be edited.
          </div>
        )}

        {/* A Sign failure (e.g. "Add your registration number…", 422) happens
            while editing is false — the Sign button only renders in that
            state — so it needs its own error slot here. Without it, `err`
            was being set by sign.mutate's onError but never rendered
            anywhere on screen in the non-editing view, which is exactly
            what made a refused sign look like clicking Sign did nothing. */}
        {!editing && err && (
          <div className="mb-3 rounded bg-danger-soft px-3 py-2 text-[12px] text-danger-ink">{err}</div>
        )}

        {/* Review attestations — the record that someone asked, which is a
            different fact from what they found. */}
        <div className="mb-4 grid gap-2 sm:grid-cols-2">
          {([
            ['allergy', 'Allergy review', data?.allergy_reviewed_at, data?.allergy_reviewed_by,
              caseSheet.allergies.length
                ? `${caseSheet.allergies.length} allergy record(s) on file`
                : 'No allergy records on file'],
            ['medication', 'Home medication reconciliation', data?.medication_reviewed_at, data?.medication_reviewed_by,
              'Reviewed against the patient’s medication list'],
          ] as const).map(([what, label, at, by, hint]) => (
            <div key={what} data-focus={`${what}_review ${what === 'allergy' ? 'allergy' : ''}`} className="rounded-lg border border-border px-3 py-2">
              <div className="flex items-center justify-between gap-2">
                <span className="text-[12.5px] font-semibold">{label}</span>
                {at ? <Badge variant="success">Reviewed</Badge> : <Badge variant="warn">Not reviewed</Badge>}
              </div>
              <div className="mt-0.5 text-[11.5px] text-ink-muted">
                {at ? `${by} · ${fmtDateTime(at)}` : hint}
              </div>
              {mayWrite && !at && (
                <Button variant="ghost" size="sm" className="mt-1.5" data-focus={`${what}_review:new`}
                  loading={review.isPending}
                  onClick={() => review.mutate(what, { onError: fail })}>
                  Mark reviewed
                </Button>
              )}
            </div>
          ))}
        </div>

        {!data && !editing ? (
          <Empty label="No admission assessment recorded"
            hint={mayWrite ? 'Start it to capture the admission workup.' : undefined} />
        ) : editing ? (
          <div className="space-y-3">
            <VoiceFormFiller
              fields={ASSESSMENT_VOICE_FIELDS}
              label="🎙 Dictate this assessment"
              hint="Admitted with fever 5 days and cough 1 week; known diabetic, hypertensive, no TB; pallor present, no icterus; chest crepitations right base; GCS 15; plan IV antibiotics"
              onFields={extracted => setDraft(p => applyAssessmentVoice(p, extracted))}
            />
            <ChiefComplaintsEditor value={draft.chief_complaints ?? []} onChange={v => set('chief_complaints', v)} />

            {NARRATIVE_FIELDS.map(f => {
              const current = (draft[f.key] as string) ?? '';
              return (
                <div key={String(f.key)}>
                  <label className="mb-1 block text-xs font-semibold">{f.label}</label>
                  <Textarea rows={f.rows ?? 2}
                    value={current}
                    onChange={e => set(f.key, e.target.value)} />
                </div>
              );
            })}

            <div>
              <div className="mb-1 text-xs font-semibold">Past history</div>
              <div className="flex flex-wrap items-center gap-2">
                {PAST_HISTORY_ITEMS.map(item => {
                  const flag = (draft.past_history_flags as PastHistoryFlags | undefined)?.[item.key];
                  return (
                    <div key={item.key} className="flex items-center gap-1 rounded-full border border-border px-2 py-0.5">
                      <span className="text-[11px] font-medium">{item.label}</span>
                      <TriChip
                        value={flag?.v}
                        options={[{ value: 'yes', label: '✔', tone: 'y' }, { value: 'no', label: '✘', tone: 'n' }, { value: 'unknown', label: '?', tone: 'u' }]}
                        onChange={(v) => setDraft(p => ({
                          ...p,
                          past_history_flags: { ...(p.past_history_flags ?? {}), [item.key]: { ...flag, v: v as TriFlag } },
                        }))}
                      />
                    </div>
                  );
                })}
              </div>
            </div>

            <div>
              <div className="mb-1 text-xs font-semibold">Personal history</div>
              <div className="grid gap-2 sm:grid-cols-3 lg:grid-cols-5">
                {(['diet', 'appetite', 'sleep', 'bowel', 'bladder'] as const).map(f => (
                  <div key={f}>
                    <label className="mb-1 block text-[11px] text-ink-muted">{f.charAt(0).toUpperCase() + f.slice(1)}</label>
                    <Input
                      value={draft.personal_history?.[f] ?? ''}
                      onChange={e => setDraft(p => ({ ...p, personal_history: { ...(p.personal_history ?? {}), [f]: e.target.value } }))} />
                  </div>
                ))}
              </div>
              <AddictionsEditor
                value={draft.personal_history?.addictions ?? []}
                onChange={v => setDraft(p => ({ ...p, personal_history: { ...(p.personal_history ?? {}), addictions: v } }))}
              />
            </div>

            {caseSheet.patient.gender?.toLowerCase() === 'female' && (
              <div>
                <div className="mb-1 text-xs font-semibold">Menstrual / obstetric</div>
                <div className="grid gap-2 sm:grid-cols-4">
                  <div><label className="mb-1 block text-[11px] text-ink-muted">LMP</label>
                    <Input type="date" value={draft.menstrual_obstetric?.lmp ?? ''}
                      onChange={e => setDraft(p => ({ ...p, menstrual_obstetric: { ...(p.menstrual_obstetric ?? {}), lmp: e.target.value } }))} /></div>
                  <div><label className="mb-1 block text-[11px] text-ink-muted">GPLA</label>
                    <div className="flex gap-1">
                      {(['g', 'p', 'l', 'a'] as const).map(k => (
                        <Input key={k} className="w-12" placeholder={k.toUpperCase()}
                          value={draft.menstrual_obstetric?.gpla?.[k] ?? ''}
                          onChange={e => setDraft(p => ({
                            ...p,
                            menstrual_obstetric: { ...(p.menstrual_obstetric ?? {}), gpla: { ...(p.menstrual_obstetric?.gpla ?? {}), [k]: Number(e.target.value) || undefined } },
                          }))} />
                      ))}
                    </div></div>
                  <div><label className="mb-1 block text-[11px] text-ink-muted">EDD (if pregnant)</label>
                    <Input type="date" value={draft.menstrual_obstetric?.edd ?? ''}
                      onChange={e => setDraft(p => ({ ...p, menstrual_obstetric: { ...(p.menstrual_obstetric ?? {}), edd: e.target.value } }))} /></div>
                  <label className="flex items-center gap-1.5 self-end pb-2 text-[12px]">
                    <input type="checkbox" checked={!!draft.menstrual_obstetric?.menopause}
                      onChange={e => setDraft(p => ({ ...p, menstrual_obstetric: { ...(p.menstrual_obstetric ?? {}), menopause: e.target.checked } }))} />
                    Menopause
                  </label>
                </div>
              </div>
            )}

            <div>
              <div className="mb-1 text-xs font-semibold">Systemic examination</div>
              <div className="grid gap-2 sm:grid-cols-2">
                {SYSTEMS.map(s => (
                  <div key={s.key}>
                    <label className="mb-1 block text-[11px] text-ink-muted">{s.label}</label>
                    <Input
                      value={((draft.systemic_examination as Record<string, string>) ?? {})[s.key] ?? ''}
                      onChange={e => setNested('systemic_examination', s.key, e.target.value)} />
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="mb-1 text-xs font-semibold">General signs</div>
              <div className="flex flex-wrap items-center gap-2">
                {GENERAL_SIGN_ITEMS.map(item => {
                  const sign = (draft.general_signs as GeneralSigns | undefined)?.[item.key];
                  return (
                    <div key={item.key} className="flex items-center gap-1 rounded-full border border-border px-2 py-0.5">
                      <span className="text-[11px] font-medium">{item.label}</span>
                      <TriChip
                        value={sign?.status}
                        options={[{ value: 'present', label: 'Present', tone: 'y' }, { value: 'absent', label: 'Absent', tone: 'n' }, { value: 'not_assessed', label: 'Not assessed', tone: 'u' }]}
                        onChange={(v) => setDraft(p => ({
                          ...p,
                          general_signs: { ...(p.general_signs ?? {}), [item.key]: { ...sign, status: v as GeneralSignStatus } },
                        }))}
                      />
                    </div>
                  );
                })}
              </div>
              <div className="mt-1 text-[10.5px] text-ink-muted">Default is "Not assessed" — a sign nobody looked for is never printed as Absent.</div>
            </div>

            <div>
              <div className="mb-1 text-xs font-semibold">GCS</div>
              <div className="flex items-end gap-3">
                {([['gcs_e', 'Eye', 4], ['gcs_m', 'Motor', 6], ['gcs_v', 'Verbal', 5]] as const).map(([key, label, max]) => (
                  <div key={key}>
                    <label className="mb-1 block text-[11px] text-ink-muted">{label} (1-{max})</label>
                    <Input type="number" min={1} max={max} className="w-16"
                      value={draft[key] ?? ''}
                      onChange={e => set(key, e.target.value ? Number(e.target.value) : undefined)} />
                  </div>
                ))}
                {draft.gcs_e && draft.gcs_m && draft.gcs_v && (
                  <div className="pb-1.5 text-[12.5px] font-semibold">= {draft.gcs_e + draft.gcs_m + draft.gcs_v} / 15</div>
                )}
              </div>
            </div>

            <div className="grid gap-2 sm:grid-cols-3">
              <div className="sm:col-span-2">
                <label className="mb-1 block text-xs font-semibold">Differential diagnosis</label>
                <Textarea rows={2} value={draft.differential_diagnosis ?? ''} onChange={e => set('differential_diagnosis', e.target.value)} />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold">Expected LOS (days)</label>
                <Input type="number" min={0} value={draft.expected_los_days ?? ''}
                  onChange={e => set('expected_los_days', e.target.value ? Number(e.target.value) : undefined)} />
              </div>
            </div>
            <label className="flex items-center gap-1.5 text-[12.5px]">
              <input type="checkbox" checked={!!draft.counselling_done} onChange={e => set('counselling_done', e.target.checked)} />
              Counselling done
            </label>

            <div>
              <div className="mb-1 text-xs font-semibold">Risk assessment</div>
              <div className="grid gap-2 sm:grid-cols-3">
                {RISKS.map(r => (
                  <div key={r.key}>
                    <label className="mb-1 block text-[11px] text-ink-muted">{r.label}</label>
                    <Input placeholder="low / moderate / high"
                      value={((draft.risk_assessment as Record<string, string>) ?? {})[r.key] ?? ''}
                      onChange={e => setNested('risk_assessment', r.key, e.target.value)} />
                  </div>
                ))}
              </div>
            </div>

            <BackdateField value={draft.assessed_at} onChange={v => set('assessed_at', v)} label="Assessed at" />

            {err && <div className="rounded bg-danger-soft px-3 py-2 text-[12px] text-danger-ink">{err}</div>}

            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => { setEditing(false); setErr(null); }}>Cancel</Button>
              <Button size="sm" loading={save.isPending} onClick={() => {
                setErr(null);
                save.mutate({ input: draft }, { onSuccess: () => setEditing(false), onError: fail });
              }}>Save</Button>
            </div>
          </div>
        ) : data ? (
          <div className="grid gap-3 sm:grid-cols-2">
            {!!data.chief_complaints?.length && (
              <div className="sm:col-span-2">
                <div className="text-[10px] font-bold uppercase tracking-wider text-ink-muted">Chief complaints</div>
                <div className="text-[12.5px]">
                  {data.chief_complaints.map((c, i) => (
                    <span key={i}>{i > 0 ? ' · ' : ''}{c.text}{c.duration_value ? ` — ${c.duration_value} ${c.duration_unit ?? 'days'}` : ''}</span>
                  ))}
                </div>
              </div>
            )}
            {data.past_history_flags && Object.keys(data.past_history_flags).length > 0 && (
              <div className="sm:col-span-2">
                <div className="text-[10px] font-bold uppercase tracking-wider text-ink-muted">Past history</div>
                <div className="flex flex-wrap gap-1.5 text-[12px]">
                  {PAST_HISTORY_ITEMS.filter(item => data.past_history_flags?.[item.key]).map(item => {
                    const flag = data.past_history_flags![item.key]!;
                    return (
                      <Badge key={item.key} variant={flag.v === 'yes' ? 'danger' : flag.v === 'no' ? 'success' : 'warn'}>
                        {item.label} {flag.v === 'yes' ? '✔' : flag.v === 'no' ? '✘' : '?'}{flag.year ? ` (${flag.year})` : ''}
                      </Badge>
                    );
                  })}
                </div>
              </div>
            )}
            {NARRATIVE_FIELDS.map(f => {
              const v = data[f.key as keyof typeof data] as string | null;
              return v ? (
                <div key={String(f.key)}>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-ink-muted">{f.label}</div>
                  <div className="whitespace-pre-wrap text-[12.5px]">{v}</div>
                </div>
              ) : null;
            })}
            {data.systemic_examination && Object.entries(data.systemic_examination).some(([, v]) => v) && (
              <div className="sm:col-span-2">
                <div className="text-[10px] font-bold uppercase tracking-wider text-ink-muted">Systemic examination</div>
                <ul className="text-[12.5px]">
                  {Object.entries(data.systemic_examination).filter(([, v]) => v).map(([k, v]) => (
                    <li key={k}>
                      <span className="text-ink-muted">{SYSTEMS.find(s => s.key === k)?.label ?? k}: </span>{v}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {data.general_signs && Object.values(data.general_signs).some(s => s?.status) && (
              <div className="sm:col-span-2">
                <div className="text-[10px] font-bold uppercase tracking-wider text-ink-muted">General signs</div>
                <div className="flex flex-wrap gap-1.5 text-[12px]">
                  {GENERAL_SIGN_ITEMS.filter(item => data.general_signs?.[item.key]?.status).map(item => {
                    const sign = data.general_signs![item.key]!;
                    return (
                      <Badge key={item.key} variant={sign.status === 'present' ? 'danger' : sign.status === 'absent' ? 'success' : 'warn'}>
                        {item.label}: {sign.status === 'not_assessed' ? 'Not assessed' : sign.status}
                      </Badge>
                    );
                  })}
                </div>
              </div>
            )}
            {data.gcs_e != null && data.gcs_m != null && data.gcs_v != null && (
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-ink-muted">GCS</div>
                <div className="text-[12.5px]">E{data.gcs_e} M{data.gcs_m} V{data.gcs_v} = <b>{data.gcs_e + data.gcs_m + data.gcs_v}/15</b></div>
              </div>
            )}
            {data.differential_diagnosis && (
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-ink-muted">Differential diagnosis</div>
                <div className="whitespace-pre-wrap text-[12.5px]">{data.differential_diagnosis}</div>
              </div>
            )}
            {data.risk_assessment && Object.entries(data.risk_assessment).some(([, v]) => v) && (
              <div className="sm:col-span-2">
                <div className="text-[10px] font-bold uppercase tracking-wider text-ink-muted">Risk assessment</div>
                <div className="flex flex-wrap gap-2 text-[12.5px]">
                  {Object.entries(data.risk_assessment).filter(([, v]) => v).map(([k, v]) => (
                    <Badge key={k} variant={v === 'high' ? 'danger' : v === 'moderate' ? 'warn' : 'neutral'}>
                      {RISKS.find(r => r.key === k)?.label ?? k}: {v}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : null}
      </Section>

      <DiagnosesPanel admissionId={admissionId} readOnly={readOnly} />
    </div>
  );
}

// ── Admission diagnoses (hybrid free-text + optional ICD-10) ────────
// Phase 4 of docs/HMS_V4_IP_CASE_SHEET_INDIA_DESIGN.md (§5.2/§6.9). Lives
// beside the admission assessment, not as its own tab — matches the
// wireframe, which places "Diagnosis & plan" inside Part A, never as a
// separate section of the tab bar.
const DIAGNOSIS_ROLES: { value: DiagnosisRole; label: string }[] = [
  { value: 'provisional', label: 'Provisional' },
  { value: 'differential', label: 'Differential' },
  { value: 'working', label: 'Working' },
  { value: 'principal', label: 'Principal' },
  { value: 'secondary', label: 'Secondary' },
  { value: 'comorbidity', label: 'Comorbidity' },
  { value: 'complication', label: 'Complication' },
];

function Icd10Picker({ onPick }: { onPick: (code: string, description: string) => void }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState<{ code: string; description: string }[]>([]);
  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setResults([]); return; }
    const handle = setTimeout(() => {
      api.get<{ code: string; description: string }[]>('/icd10', { query: { q: term, limit: 8 } })
        .then(setResults).catch(() => setResults([]));
    }, 250);
    return () => clearTimeout(handle);
  }, [q]);

  return (
    <div className="relative">
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search ICD-10 (code or text) — optional" />
      {results.length > 0 && (
        <div className="absolute z-10 mt-1 w-full rounded-lg border border-border bg-surface shadow-lg">
          {results.map((r) => (
            <button key={r.code} type="button"
              className="block w-full px-3 py-1.5 text-left text-[12.5px] hover:bg-muted"
              onClick={() => { onPick(r.code, r.description); setQ(''); setResults([]); }}>
              <b>{r.code}</b> — {r.description}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function DiagnosesPanel({ admissionId, readOnly }: { admissionId: number; readOnly: boolean }) {
  const can = useCan();
  const mayWrite = can('ip:diagnosis') && !readOnly;
  const { data, isLoading } = useDiagnoses(admissionId);
  const create = useCreateDiagnosis(admissionId);
  const supersede = useSupersedeDiagnosis(admissionId);
  const [adding, setAdding] = useState(false);
  const [role, setRole] = useState<DiagnosisRole>('provisional');
  const [text, setText] = useState('');
  const [code, setCode] = useState('');
  const [err, setErr] = useState<string | null>(null);
  // Correct asks in a dialog — window.prompt() is refused by embedded browsers.
  const [correcting, setCorrecting] = useState<{ id: number; text: string } | null>(null);
  const fail = (e: unknown) => setErr(e instanceof ApiError ? e.message : 'Something went wrong.');

  const current = (data ?? []).filter((d) => !d.superseded_by_id);
  const principal = current.find((d) => d.role === 'principal');

  const reset = () => { setRole('provisional'); setText(''); setCode(''); };

  if (isLoading) return <Section title="Diagnoses"><LoadingState label="Loading diagnoses…" /></Section>;

  return (
    <Section
      focusId="diagnosis"
      title="Diagnosis & plan"
      subtitle={principal ? `Principal: ${principal.text}${principal.code ? ` [${principal.code}]` : ''}` : 'No principal diagnosis recorded yet'}
      action={mayWrite && !adding && <Button size="sm" data-focus="diagnosis:new" onClick={() => setAdding(true)}>+ Diagnosis</Button>}
    >
      {/* "Correct" (below, on an existing row) sets err too, but outside
          `adding` — without this, a failed correction looked like nothing
          happened, same bug as the Sign button above. */}
      {!adding && err && (
        <div className="mb-3 rounded bg-danger-soft px-3 py-2 text-[12px] text-danger-ink">{err}</div>
      )}

      {adding && (
        <div className="mb-3 space-y-2 rounded-lg border border-border bg-muted/40 p-3">
          <div className="grid gap-2 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-semibold">Role</label>
              <NativeSelect value={role} onChange={(e) => setRole(e.target.value as DiagnosisRole)}>
                {DIAGNOSIS_ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </NativeSelect>
            </div>
            <div><label className="mb-1 block text-xs font-semibold">ICD-10 code (optional)</label>
              <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="e.g. J18.1" /></div>
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold">Diagnosis text</label>
            <Textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder="Always free text — the ICD-10 code is an assist, never required" />
          </div>
          <Icd10Picker onPick={(c, desc) => { setCode(c); if (!text.trim()) setText(desc); }} />
          {err && <div className="rounded bg-danger-soft px-3 py-2 text-[12px] text-danger-ink">{err}</div>}
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => { setAdding(false); setErr(null); reset(); }}>Cancel</Button>
            <Button size="sm" loading={create.isPending} disabled={!text.trim()}
              onClick={() => {
                setErr(null);
                create.mutate({ role, text, code: code || undefined }, {
                  onSuccess: () => { setAdding(false); reset(); },
                  onError: fail,
                });
              }}>Save</Button>
          </div>
        </div>
      )}

      {!data?.length ? <Empty label="No diagnoses recorded" /> : (
        <div className="space-y-1.5">
          {current.map((d) => (
            <div key={d.id} className="flex flex-wrap items-center gap-2 rounded-lg border border-border px-3 py-1.5">
              <Badge variant={d.role === 'principal' ? 'success' : 'neutral'}>
                {DIAGNOSIS_ROLES.find((r) => r.value === d.role)?.label ?? d.role}
              </Badge>
              <span className="text-[12.5px]">{d.text}{d.code ? ` [${d.code}]` : ''}</span>
              <span className="text-[11px] text-ink-muted">{fmtDateTime(d.recorded_at)}</span>
              {mayWrite && (
                <Button variant="ghost" size="sm" className="ml-auto"
                  onClick={() => { supersede.reset(); setCorrecting({ id: d.id, text: d.text }); }}>Correct</Button>
              )}
            </div>
          ))}
        </div>
      )}
      <TextPromptDialog
        open={!!correcting}
        title="Correct this diagnosis?"
        description="The current entry is kept as history and replaced by the corrected text."
        label="Corrected diagnosis text"
        initialValue={correcting?.text ?? ''}
        maxLength={500}
        confirmLabel="Save correction"
        pending={supersede.isPending}
        error={supersede.error instanceof Error ? supersede.error.message : null}
        onConfirm={(newText) => {
          if (!correcting) return;
          setErr(null);
          supersede.mutate({ diagnosisId: correcting.id, changes: { text: newText } }, { onSuccess: () => setCorrecting(null) });
        }}
        onClose={() => setCorrecting(null)}
      />
    </Section>
  );
}
